/**
 * Event appliers — one pure function per `WorldEvent` variant.
 *
 * Each applier mutates the passed `WorldModel` in place and returns it (for
 * chaining in reducers). Zustand's immer-free model shape is intentional:
 * dense `Uint8Array` tile buffers, shared-reference `Map` entity stores,
 * and BigInt ticks all resist structural-sharing helpers without payoff.
 * Callers opt in to store subscription granularity by reading narrow
 * selectors, not by relying on deep-equal diffs (CLIENT_DESIGN §17.2).
 *
 * Determinism: applying the same event list to a cloned starting model
 * produces an identical resulting model. Tested via snapshot round-trip in
 * `appliers.test.ts`.
 *
 * Phase-2+ variants (SharedFood, ComfortGiven, Died, Slept, Dreamed, Fought,
 * RestStarted/Ended, ArousalChanged, RelationshipChanged, MemoryConsolidated)
 * currently no-op; their appliers exist so `applyEvent` is total over the
 * generated `WorldEvent` catalog.
 */

import { phase0, debugLog } from '../log';
import type {
  EntityId,
  EventEnvelope,
  PlayerSnapshot,
  SnapshotMessage,
  WorldEvent,
} from '../wire';
import {
  createEmptyWorldModel,
  edgeKeyString,
  fogBitSet,
  tileIndex,
  type ClockState,
  type PlayerEntry,
  type TimeOfDayPhase,
  type WeatherState,
  type WorldModel,
} from './model';

const MAX_RECENT_UTTERANCES = 5; // CLIENT_DESIGN §11.6
const MAX_BUBBLES_PER_SPEAKER = 3; // CLIENT_DESIGN §12.3 / §11.3

/** Module-local monotonic id generator for `SpeechBubble.id`. Independent
 *  of the server tick — the renderer uses it as a stable key into its
 *  per-bubble wall-clock timer map. */
let nextBubbleId = 1;

function toUint8Array(data: Uint8Array | number[]): Uint8Array {
  return data instanceof Uint8Array ? data : new Uint8Array(data);
}

function entityKey(e: EntityId): string {
  if ('Player' in e) return `p:${e.Player}`;
  if ('Soma' in e) return `s:${e.Soma}`;
  return '?';
}

/** After appending a new bubble, retain at most `MAX_BUBBLES_PER_SPEAKER`
 *  bubbles for `speakerKey` (oldest-first drop). Other speakers untouched.
 *  Exported for unit testing. */
export function capBubblesPerSpeaker(
  bubbles: ReadonlyArray<import('./model').SpeechBubble>,
  speakerKey: string,
): import('./model').SpeechBubble[] {
  let countForSpeaker = 0;
  for (const b of bubbles) {
    if (entityKey(b.speaker) === speakerKey) countForSpeaker++;
  }
  if (countForSpeaker <= MAX_BUBBLES_PER_SPEAKER) return [...bubbles];
  const toDrop = countForSpeaker - MAX_BUBBLES_PER_SPEAKER;
  let dropped = 0;
  return bubbles.filter((b) => {
    if (dropped >= toDrop) return true;
    if (entityKey(b.speaker) === speakerKey) {
      dropped++;
      return false;
    }
    return true;
  });
}

// ─── Utilities ────────────────────────────────────────────────────────────

function ensurePlayer(model: WorldModel, id: number): PlayerEntry {
  let p = model.players.get(id);
  if (!p) {
    p = {
      id,
      display_name: `player-${id}`,
      position: [0, 0],
      facing: 'South',
      role: 'Player',
      connected: true,
      recent_utterances: [],
    };
    model.players.set(id, p);
  }
  return p;
}

function entityIsPlayer(entity: EntityId): entity is { Player: number } {
  return 'Player' in entity;
}

function entityPlayerId(entity: EntityId): number | null {
  return entityIsPlayer(entity) ? entity.Player : null;
}

// ─── Per-variant appliers ─────────────────────────────────────────────────

// The WorldEvent enum is externally-tagged (`{ "WallPlaced": { ... } }`) per
// ts-rs default. `applyEvent` dispatches on the single key.

export function applyEvent(model: WorldModel, event: WorldEvent): WorldModel {
  // Each branch destructures the payload.
  if ('WallPlaced' in event) {
    const { edge, material, placed_by } = event.WallPlaced;
    model.walls.set(edgeKeyString(edge), {
      edge,
      occupant: { kind: 'wall', material, placed_by: null },
    });
    // Note: `placed_by` on the wire is SomaId; Phase 1 players don't populate
    // this directly (P1.4 will widen per Q-05), so we leave it null for now.
    void placed_by;
    return model;
  }

  if ('WallRemoved' in event) {
    model.walls.delete(edgeKeyString(event.WallRemoved.edge));
    return model;
  }

  if ('DoorPlaced' in event) {
    const { edge, material, state, placed_by } = event.DoorPlaced;
    void placed_by;
    model.walls.set(edgeKeyString(edge), {
      edge,
      occupant: { kind: 'door', material, state, placed_by: null },
    });
    return model;
  }

  if ('DoorRemoved' in event) {
    model.walls.delete(edgeKeyString(event.DoorRemoved.edge));
    return model;
  }

  if ('DoorStateChanged' in event) {
    const { edge, state } = event.DoorStateChanged;
    const key = edgeKeyString(edge);
    const existing = model.walls.get(key);
    if (existing && existing.occupant.kind === 'door') {
      existing.occupant = { ...existing.occupant, state };
    }
    return model;
  }

  if ('RoomEnclosed' in event) {
    const { room, tiles, archetype } = event.RoomEnclosed;
    model.rooms.set(room, { id: room, archetype, tiles: [...tiles] });
    for (const [x, y] of tiles) {
      if (x >= 0 && x < model.width && y >= 0 && y < model.height) {
        model.tileRoomIds[tileIndex(x, y, model.width)] = room;
      }
    }
    return model;
  }

  if ('RoomDissolved' in event) {
    const r = model.rooms.get(event.RoomDissolved.room);
    if (r) {
      for (const [x, y] of r.tiles) {
        if (x >= 0 && x < model.width && y >= 0 && y < model.height) {
          model.tileRoomIds[tileIndex(x, y, model.width)] = -1;
        }
      }
      model.rooms.delete(r.id);
    }
    return model;
  }

  if ('PropSpawned' in event) {
    const { prop, kind, position } = event.PropSpawned;
    model.props.set(prop, { id: prop, kind, position, placer: null });
    return model;
  }

  if ('PropPlaced' in event) {
    const { placer, prop, kind, position } = event.PropPlaced;
    model.props.set(prop, { id: prop, kind, position, placer });
    return model;
  }

  if ('PropRemoved' in event) {
    model.props.delete(event.PropRemoved.prop);
    return model;
  }

  // Player lifecycle
  if ('PlayerJoined' in event) {
    const { player, position, display_name } = event.PlayerJoined;
    const p = ensurePlayer(model, player);
    p.display_name = display_name;
    p.position = position;
    p.connected = true;
    return model;
  }

  if ('PlayerReconnected' in event) {
    const p = ensurePlayer(model, event.PlayerReconnected.player);
    p.connected = true;
    return model;
  }

  if ('PlayerDisconnected' in event) {
    const p = ensurePlayer(model, event.PlayerDisconnected.player);
    p.connected = false;
    return model;
  }

  if ('PlayerDespawned' in event) {
    model.players.delete(event.PlayerDespawned.player);
    return model;
  }

  if ('PlayerMoved' in event) {
    const { player, to, facing } = event.PlayerMoved;
    const p = ensurePlayer(model, player);
    p.position = to;
    p.facing = facing;
    return model;
  }

  if ('PlayerRelocated' in event) {
    const { player, to } = event.PlayerRelocated;
    const p = ensurePlayer(model, player);
    p.position = to;
    return model;
  }

  if ('PlayerRoleChanged' in event) {
    const { player, new_role } = event.PlayerRoleChanged;
    const p = ensurePlayer(model, player);
    p.role = new_role;
    return model;
  }

  if ('PlayerKicked' in event) {
    model.players.delete(event.PlayerKicked.target);
    return model;
  }

  if ('PlayerIntentThrottled' in event) {
    // Local-only indicator; CLIENT_DESIGN §4.9. Model carries no state for
    // this — the UI layer subscribes to the event stream separately.
    return model;
  }

  // Harvest + craft — inventory deltas flow into a separate inventory slice
  // (landing with D9/D10 UI). The world model only records the world-side
  // transition; the holder-side change is in `inventory_delta` and consumed
  // by the inventory store.
  if ('Harvested' in event) {
    // No world-side state change beyond the prop (which the emitter also
    // sends a PropRemoved for). Optional: track harvest flash here.
    return model;
  }
  if ('CraftCompleted' in event) return model;
  if ('CraftFailed' in event) return model;
  if ('Built' in event) return model;

  // Weather — bridge wire payloads into the expanded M2.1 local shape.
  // The wire `WeatherChanged` / `WeatherTick` carry the Phase 1 subset
  // (kind, intensity, target_at). Extended fields (temperature, humidity,
  // etc.) are present if the server sends them; otherwise we carry forward
  // the previous values so M2.1 client features degrade gracefully.
  if ('WeatherChanged' in event) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- wire shape may differ from local
    model.weather = bridgeWeatherEvent(model.weather, event.WeatherChanged as any);
    return model;
  }
  if ('WeatherTick' in event) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- wire shape may differ from local
    model.weather = bridgeWeatherEvent(model.weather, event.WeatherTick as any);
    return model;
  }

  // Fog-of-war reveal for the viewing player
  if ('TileRevealed' in event) {
    const { position } = event.TileRevealed;
    const [x, y] = position;
    if (x >= 0 && x < model.width && y >= 0 && y < model.height) {
      fogBitSet(model.fog, tileIndex(x, y, model.width), true);
      phase0.debug('fog_update', { x, y });
      debugLog('world', 'debug', `fog_reveal x=${x} y=${y}`);
    }
    return model;
  }

  // Chat
  if ('Spoke' in event) {
    const { speaker, utterance, position, zone } = event.Spoke;
    const pid = entityPlayerId(speaker);
    if (pid !== null) {
      const p = ensurePlayer(model, pid);
      p.recent_utterances = [utterance, ...p.recent_utterances].slice(
        0,
        MAX_RECENT_UTTERANCES,
      );
    }
    const speakerKey = entityKey(speaker);
    const nextBubble = {
      id: nextBubbleId++,
      speaker,
      utterance,
      zone,
      position,
      tick: model.tick,
    };
    // CLIENT_DESIGN §12.3 cap: keep at most MAX_BUBBLES_PER_SPEAKER active
    // bubbles per speaker — newest pushes oldest.
    model.speech_bubbles = capBubblesPerSpeaker(
      [...model.speech_bubbles, nextBubble],
      speakerKey,
    );
    return model;
  }
  if ('ZoneSystemMessage' in event) {
    // Rendered as a system line in the chat pane; no world-model effect.
    return model;
  }
  if ('ChatIntentRejected' in event) {
    // Inline error in the chat input; no world-model effect.
    return model;
  }

  // Phase 2+ stubs — no-op in Phase 1.
  if ('SharedFood' in event) return model;
  if ('ComfortGiven' in event) return model;
  if ('Died' in event) return model;
  if ('Slept' in event) return model;
  if ('Dreamed' in event) return model;
  if ('Fought' in event) return model;
  if ('RestStarted' in event) return model;
  if ('RestEnded' in event) return model;
  if ('ArousalChanged' in event) return model;
  if ('RelationshipChanged' in event) return model;
  if ('MemoryConsolidated' in event) return model;

  // `WorldEvent` is `#[non_exhaustive]` on the Rust side; a future variant
  // the client does not recognise reaches this branch. Per WIRE_PROTOCOL
  // §12.3, log and skip.
  return model;
}

// ─── Batch + envelope appliers ────────────────────────────────────────────

/** Apply a single server event batch; sets `model.tick` to the batch tick. */
export function applyEventBatch(
  model: WorldModel,
  tick: bigint,
  events: WorldEvent[],
): WorldModel {
  model.tick = tick;
  // Derive clock fields from the tick (M2.1 client-side derivation until
  // the server ships full ClockState per event batch).
  model.clock = deriveClockFromTick(tick, model.clock);
  for (const event of events) applyEvent(model, event);
  return model;
}

/**
 * Apply an `EventEnvelope` if it is a `WorldEvents` variant (the only kind
 * that affects world state). Out-of-world envelopes (DM, chat broadcast,
 * etc.) flow to dedicated slices and are ignored here.
 */
export function applyEnvelope(
  model: WorldModel,
  envelope: EventEnvelope,
): WorldModel {
  if (envelope.envelope_kind === 'WorldEvents') {
    applyEventBatch(model, envelope.data.tick, envelope.data.events);
  }
  return model;
}

// ─── Snapshot hydration ───────────────────────────────────────────────────

/**
 * Hydrate a world model from the server snapshot (Fresh or Full reconnect).
 * Overwrites all world-model fields except the BigInt precision semantics.
 *
 * P1.8 C5: reads the record-list `WorldSnapshot` (walls / doors / props /
 * rooms) rather than the retired hand-rolled dense-tile form. Dense
 * `tileMaterials` / `tileBiomes` buffers are zero-initialised — they're
 * no longer carried on the wire, and the tile renderer reads them as
 * 0 → `MaterialId(0)` per `pixi/palette.ts`. A future deliverable can
 * derive tile materials from the biome record-set or a shared
 * client-worldgen; for Phase 1 the renderer paints a uniform floor
 * (matching current mock behaviour).
 */
export function applySnapshot(
  _prev: WorldModel,
  snapshot: SnapshotMessage,
): WorldModel {
  const model = createEmptyWorldModel();
  const [width, height] = snapshot.world.bounds;
  model.width = width;
  model.height = height;
  model.tick = snapshot.tick;
  // Bridge the wire ClockState (Phase 1 shape: { tick, hour, day, season })
  // into the M2.1 local shape (phase, day_tick, season_day, year). When the
  // server ships M2.1 bindings with the new fields they flow through
  // naturally; until then we derive from `hour`.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- wire shape may differ from local
  model.clock = bridgeClockState(snapshot.world.clock as any, snapshot.tick);

  const count = width * height;
  // Tile buffers — tileMaterials and tileBiomes are zero-filled (not on
  // the wire). tileElevations hydrated from snapshot.world.tile_elevations
  // (M2.2 addition).
  model.tileMaterials = new Uint8Array(count);
  model.tileBiomes = new Uint8Array(count);
  const wireElevations = snapshot.world.tile_elevations;
  if (wireElevations && wireElevations.length >= count) {
    model.tileElevations = new Uint8Array(wireElevations);
  } else if (wireElevations && wireElevations.length > 0) {
    const padded = new Uint8Array(count);
    padded.set(wireElevations);
    model.tileElevations = padded;
  } else {
    model.tileElevations = new Uint8Array(count);
  }
  model.tileRoomIds = new Int32Array(count).fill(-1);

  // Walls (WallRecord — per-wall placed_by is wire-side `EntityId | null`;
  // the model's wall-occupant narrows to `PlayerId | null`, so SOMA-placed
  // walls hydrate with `null` pending the Q-05 widening).
  for (const w of snapshot.world.walls) {
    model.walls.set(edgeKeyString(w.edge), {
      edge: w.edge,
      occupant: {
        kind: 'wall',
        material: w.material,
        placed_by:
          w.placed_by && 'Player' in w.placed_by ? w.placed_by.Player : null,
      },
    });
  }

  // Doors (DoorRecord — door-occupant keeps the full EntityId).
  for (const d of snapshot.world.doors) {
    model.walls.set(edgeKeyString(d.edge), {
      edge: d.edge,
      occupant: {
        kind: 'door',
        material: d.material,
        state: d.state,
        placed_by: d.placed_by,
      },
    });
  }

  // Props (PropRecord). The wire doesn't carry a placer; snapshot-time
  // props land with `placer: null` — subsequent `PropPlaced` events
  // populate it as they fire.
  for (const p of snapshot.world.props) {
    model.props.set(p.id, {
      id: p.id,
      kind: p.kind,
      position: p.position,
      placer: null,
    });
  }

  // Rooms (RoomRecord). Also back-fill `tileRoomIds` for each tile in
  // the room, mirroring the `RoomEnclosed` event applier.
  for (const r of snapshot.world.rooms) {
    model.rooms.set(r.id, {
      id: r.id,
      archetype: r.archetype,
      tiles: [...r.tiles],
    });
    for (const [x, y] of r.tiles) {
      if (x >= 0 && x < width && y >= 0 && y < height) {
        model.tileRoomIds[tileIndex(x, y, width)] = r.id;
      }
    }
  }

  // Players
  for (const s of snapshot.players) hydratePlayer(model, s);

  // Ambient — moved to top-level in the record-list SnapshotMessage.
  // Bridge the 3-field wire WeatherSnapshot into the expanded M2.1 local
  // WeatherState. When the server ships expanded fields they flow through.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- wire shape may differ from local
  model.weather = bridgeWeatherSnapshot(snapshot.weather as any);
  const fogBytes = Math.ceil(count / 8);
  const incoming = toUint8Array(snapshot.fog.bitmap);
  if (incoming.length >= fogBytes) {
    model.fog = incoming.slice(0, fogBytes);
  } else {
    const padded = new Uint8Array(fogBytes);
    padded.set(incoming);
    model.fog = padded;
  }

  return model;
}

// ─── M2.1 bridge helpers ─────────────────────────────────────────────────
// These bridge the Phase 1 wire shapes (ClockState with `hour`,
// WeatherSnapshot with 3 fields) into the M2.1 local shapes. When the
// server ships the expanded ts-rs bindings, the extra fields flow through
// the spread and the derivation fallbacks become no-ops.

/** Clock config defaults — matches CLOCK.md §11 and config.toml. */
const TICKS_PER_DAY = 2400;
const DAYS_PER_SEASON = 10;
const DAWN_START = 200;
const DAY_START = 400;
const DUSK_START = 1400;
const NIGHT_START = 1600;

function phaseFromDayTick(dayTick: number): TimeOfDayPhase {
  if (dayTick < DAWN_START) return 'Night';
  if (dayTick < DAY_START) return 'Dawn';
  if (dayTick < DUSK_START) return 'Day';
  if (dayTick < NIGHT_START) return 'Dusk';
  return 'Night';
}

/** Derive a full M2.1 ClockState from a tick number. */
function deriveClockFromTick(tick: bigint, _prev: ClockState): ClockState {
  const tickNum = Number(tick);
  const dayTick = tickNum % TICKS_PER_DAY;
  const totalDays = Math.floor(tickNum / TICKS_PER_DAY);
  const daysPerYear = DAYS_PER_SEASON * 4;
  const year = Math.floor(totalDays / daysPerYear);
  const dayInYear = totalDays % daysPerYear;
  const seasonIndex = Math.floor(dayInYear / DAYS_PER_SEASON);
  const seasonDay = dayInYear % DAYS_PER_SEASON;
  const seasons = ['Spring', 'Summer', 'Autumn', 'Winter'] as const;
  return {
    tick,
    day_tick: dayTick,
    day: totalDays,
    season: seasons[seasonIndex] ?? 'Spring',
    season_day: seasonDay,
    year,
    phase: phaseFromDayTick(dayTick),
  };
}

/** Bridge the wire ClockState into the M2.1 local shape. If the wire
 *  carries M2.1 fields (day_tick, phase, etc.) they flow through; otherwise
 *  we derive from `hour` or from `tick`. */
function bridgeClockState(
  wire: Record<string, unknown>,
  tick: bigint,
): ClockState {
  // If the wire already has M2.1 fields, use them directly.
  if ('day_tick' in wire && 'phase' in wire) {
    return {
      tick,
      day_tick: wire.day_tick as number,
      day: wire.day as number,
      season: wire.season as ClockState['season'],
      season_day: (wire.season_day as number) ?? 0,
      year: (wire.year as number) ?? 0,
      phase: wire.phase as TimeOfDayPhase,
    };
  }
  // Phase 1 fallback: derive from tick.
  return deriveClockFromTick(tick, createEmptyWorldModel().clock);
}

/** Default weather state for fields the wire may not carry yet. */
const DEFAULT_WEATHER: WeatherState = {
  kind: 'Clear', intensity: 0, target_at: 0n,
  temperature: 20, apparent_temperature: 20, humidity: 0.5, pressure: 0.5,
  wind_direction: 0, wind_speed: 0, cloud_cover: 0, precip_intensity: 0,
  precip_type: 'None', thunderstorm_active: false, fog_density: 0, visibility: 1,
};

/** Bridge a 3-field wire WeatherSnapshot into the expanded local shape. */
function bridgeWeatherSnapshot(
  wire: Record<string, unknown>,
): WeatherState {
  return {
    ...DEFAULT_WEATHER,
    ...wire,
  } as WeatherState;
}

/** Bridge a WeatherChanged / WeatherTick event into the expanded local shape.
 *  Carries forward previous extended fields if the event doesn't include them. */
function bridgeWeatherEvent(
  prev: WeatherState,
  wire: Record<string, unknown>,
): WeatherState {
  return {
    ...prev,
    ...wire,
  } as WeatherState;
}

function hydratePlayer(model: WorldModel, snap: PlayerSnapshot): void {
  // P1.8 C5: generated PlayerSnapshot fields — `id`, `connection_status`,
  // plus `facing` / `role` the hand-rolled shape lacked.
  model.players.set(snap.id, {
    id: snap.id,
    display_name: snap.display_name,
    position: snap.position,
    facing: snap.facing,
    role: snap.role,
    connected: snap.connection_status === 'Connected',
    recent_utterances: [],
  });
}

/**
 * Replay path — server sent a `Replay { events_since }` HandshakeAck, so the
 * client preserves its local state and applies catch-up events in order.
 */
export function applyReplay(
  model: WorldModel,
  events: WorldEvent[],
  newestTick: bigint,
): WorldModel {
  for (const e of events) applyEvent(model, e);
  if (newestTick > model.tick) {
    model.tick = newestTick;
    model.clock = deriveClockFromTick(newestTick, model.clock);
  }
  return model;
}
